import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useMemo,
  lazy,
  Suspense,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useStore } from '@tanstack/react-form'
import {
  ArrowBack,
  Cancel,
  Add,
  CheckCircle,
  CancelOutlined,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import { MIN_BUTTON_SIZE } from 'src/v3/manage/constants'
import { ConfirmIcon, DeleteIcon, EditIcon } from 'src/v3/core/icons'
import ImageUpload from 'src/v3/components/upload-image'
import NumberField from 'src/v3/components/form-fields/number-field'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import CardSectionHeader from 'src/v3/components/card-section-header'
import {
  fetchCategories,
  fetchCourses,
  fetchLinkedServices,
  fetchReferences,
  fetchSelectedCourse,
  fetchTemplates,
  fetchVideoLibrary,
  fetchShowNotesLibrary,
  fetchCourseStepsByCourseID,
  fetchQuizLibrary,
  selectCourseFormLoadingStatuses,
  selectCategoriesByLinkedService,
  selectLinkedServices,
  selectReferencesByLinkedService,
  selectSelectedCourse,
  selectTemplatesByLinkedService,
  selectedLinkedServiceChanged,
  selectLegacyContentData,
  selectVideoLibrary,
  selectVideoLibraryStatus,
  selectShowNotesLibrary,
  selectShowNotesLibraryStatus,
  selectCourseSteps,
  selectCourseStepsStatus,
  selectQuizLibrary,
  selectQuizLibraryStatus,
  selectAccreditationsByCourseID,
  fetchAllAccreditations,
  fetchAllInstructors,
  selectInstructors,
  selectInstructorsByCourseID,
  selectInstructorsStatus,
} from '../create.slice'
import {
  contentTypeToTextMap,
  CREATE_COURSE_URLS,
  CREATE_QUIZ_URLS,
  CREATE_SHOW_NOTES_URLS,
  paperInputSx,
} from '../constants'
import { courseFormSchema } from './validation'
import MigratableContentList from './components/migratable-content-list'
import {
  ObjectivesReference,
  VideoCourseRef,
  ShowNotesCourseRef,
  CoursePublishedStatus,
  CreateContentTypes,
} from './types'
import { getDirtySections, validateSections } from './utils/form-state'
import {
  migrateAccreditation,
  removeCourseAccreditation,
  updateCoursePublicationStatus,
} from '../utils/api'
import { usePublishValidation } from './hooks/use-publish-validation'
import { usePublishGuard } from './hooks/use-publish-guard'
import { useCourseImages } from './hooks/use-course-images'
import { useModalState } from './hooks/use-modal-state'
import { useInstructorActions } from './hooks/use-instructor-actions'
import { useCourseFormSubmission } from './hooks/use-course-form-submission'
import FormFieldError from './components/form-field-error'
import CourseStepsSection from './components/course-steps-section'
import AccreditationsSection from './components/accreditations-section'
import InstructorsSection from './components/instructors-section'
import ConfirmDialog from './components/modals/confirm-dialog'
import UnpublishRequiredDialog from './components/modals/unpublish-required-dialog'

// Lazy load modal components to reduce initial bundle size
let CategoryModal = lazy(() => import('./components/modals/category-modal'))
let TemplateModal = lazy(() => import('./components/modals/template-modal'))
let VideoModal = lazy(() => import('./components/modals/video-modal'))
let VideoPreviewModal = lazy(
  () => import('./components/modals/video-preview-modal'),
)
let ShowNotesModal = lazy(() => import('./components/modals/show-notes-modal'))
let ShowNotesPreviewModal = lazy(
  () => import('./components/modals/show-notes-preview-modal'),
)
let QuizModal = lazy(() => import('./components/modals/quiz-modal'))
let QuizPreviewModal = lazy(
  () => import('./components/modals/quiz-preview-modal'),
)
let CourseTitleMigrationScreenModal = lazy(
  () => import('./components/modals/migrate-title-screen'),
)
let MigrateTextContentModal = lazy(
  () => import('./components/modals/migrate-text-content-modal'),
)
let EditLibraryReferencesModal = lazy(
  () => import('./components/modals/edit-library-references-modal'),
)
let AccreditationModal = lazy(
  () => import('./components/modals/accreditation-modal'),
)
let InstructorModal = lazy(() => import('./components/modals/instructor-modal'))

function PublishChecklistItem({
  done,
  label,
}: {
  done: boolean
  label: string
}): ReactElement {
  return (
    <Box className="flex items-center gap-2">
      {done ? (
        <CheckCircle
          fontSize="small"
          sx={{
            color: 'success.main',
          }}
        />
      ) : (
        <CancelOutlined
          fontSize="small"
          sx={{
            color: 'error.main',
          }}
        />
      )}
      <Typography
        variant="body2"
        sx={{ color: done ? 'text.primary' : 'text.secondary' }}
      >
        {label}
      </Typography>
    </Box>
  )
}

export default function CourseV5Form(): ReactElement {
  useScrollToTop()

  let { courseID } = useParams<{ courseID: string }>()
  let courseDetails = useAppSelector(selectSelectedCourse)
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let isEditMode = !!courseID

  usePageTour('edit-course', isEditMode)

  let linkedServices = useAppSelector(selectLinkedServices)
  let loadingStatuses = useAppSelector(selectCourseFormLoadingStatuses)

  // Group legacy content selectors
  let legacyContent = useAppSelector(selectLegacyContentData)
  let {
    textContent: legacyTextContent,
    titleScreens: legacyTitleScreens,
    steps: legacySteps,
  } = legacyContent
  let legacyClassID = courseDetails?.legacyClassID || ''
  let legacyTitleScreen = legacyTitleScreens[0] || null

  // Current V5 content from the three libraries
  let courseSteps = useAppSelector(selectCourseSteps)
  let courseStepsStatus = useAppSelector(selectCourseStepsStatus)
  let videos = useAppSelector(selectVideoLibrary)
  let videoLibraryStatus = useAppSelector(selectVideoLibraryStatus)
  let courseVideos = videos.filter((v) =>
    v.courses.some((c: VideoCourseRef) => c.courseID === courseID),
  )
  let videosNeedingMigration = courseVideos.filter((v) => v.isMigratable)
  let showNotesLibrary = useAppSelector(selectShowNotesLibrary)
  let showNotesLibraryStatus = useAppSelector(selectShowNotesLibraryStatus)
  let courseShowNotes = showNotesLibrary.filter((sn) =>
    sn.courses.some((c: ShowNotesCourseRef) => c.courseID === courseID),
  )
  let showNotesNeedingMigration = courseShowNotes.filter(
    (sn) => sn.isMigratable,
  )
  let quizLibrary = useAppSelector(selectQuizLibrary)
  let quizLibraryStatus = useAppSelector(selectQuizLibraryStatus)
  let courseQuizzes = quizLibrary.filter((q) =>
    q.courses.some((c) => c.courseID === courseID),
  )
  let quizzesNeedingMigration = courseQuizzes.filter((q) => q.isMigratable)
  let hasQuiz = courseQuizzes.length > 0

  let hasMigratableContent =
    (!!legacyClassID && legacyTextContent.length > 0) ||
    videosNeedingMigration.length > 0 ||
    showNotesNeedingMigration.length > 0 ||
    quizzesNeedingMigration.length > 0

  let isPublished = courseDetails?.published === 'published'

  let isLoading =
    loadingStatuses.linkedServices === 'pending' ||
    loadingStatuses.categories === 'pending' ||
    loadingStatuses.templates === 'pending' ||
    loadingStatuses.references === 'pending' ||
    (isEditMode && loadingStatuses.selectedCourse === 'pending')

  let {
    banner,
    setBanner,
    thumbnail,
    setThumbnail,
    existingBannerImage,
    existingThumbnailImage,
    bannerImageBasePath,
    thumbnailImageBasePath,
  } = useCourseImages({ courseDetails, isEditMode, isLoading })
  let {
    activeModal,
    selectedTextContentID,
    openModal,
    closeModal,
    openTextContentModal,
    selectedEditTemplateID,
    openEditTemplateModal,
    selectedEditCategoryID,
    openEditCategoryModal,
    selectedEditVideoID,
    openEditVideoModal,
    selectedPreviewVideoID,
    openVideoPreviewModal,
    openAddShowNotesModal,
    selectedPreviewShowNotesID,
    openShowNotesPreviewModal,
    selectedPreviewQuizID,
    openQuizPreviewModal,
    selectedAccreditation,
    openAccreditationModal,
    selectedEditInstructor,
    createInstructorPrefillName,
    openCreateInstructorModal,
    openEditInstructorModal,
    isCreateInstructorModalOpen,
    isEditInstructorModalOpen,
  } = useModalState()

  let [isStepsSaving, setIsStepsSaving] = useState(false)
  let [isMigratingAccreditation, setIsMigratingAccreditation] = useState(false)

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let { handleSubmit: submitCourse } = useCourseFormSubmission({
    isEditMode,
    courseID,
    courseDetails,
    banner,
    thumbnail,
    dispatch,
    navigate,
    showSuccess,
    showError,
  })

  useAutoFetch(loadingStatuses.linkedServices, fetchLinkedServices)
  useAutoFetch(loadingStatuses.categories, fetchCategories)
  // Only fetch courses list in create mode, not when editing a specific course
  useAutoFetch(
    !isEditMode ? loadingStatuses.courses : 'fulfilled',
    fetchCourses,
  )
  useAutoFetch(loadingStatuses.templates, fetchTemplates)
  useAutoFetch(loadingStatuses.references, fetchReferences)
  useAutoFetch(videoLibraryStatus, fetchVideoLibrary)
  useAutoFetch(showNotesLibraryStatus, fetchShowNotesLibrary)
  useAutoFetch(quizLibraryStatus, fetchQuizLibrary)

  useEffect(() => {
    if (isEditMode && courseID) {
      dispatch(fetchSelectedCourse(courseID))
      dispatch(fetchAllAccreditations())
    }
  }, [isEditMode, courseID, dispatch])

  let defaultValues = useMemo(() => {
    if (isEditMode && courseDetails && !isLoading) {
      return {
        title: courseDetails.name || '',
        linkedServiceID: courseDetails.linkedServiceID || '',
        sectionID: courseDetails.sectionID || '',
        templateID: courseDetails.templateID || '',
        length: courseDetails.length ?? 1,
        description: courseDetails.description || '',
        objectives: (courseDetails.objectives || []) as ObjectivesReference[],
        references: (courseDetails.references || []) as ObjectivesReference[],
        banner: '',
        thumbnail: '',
      }
    }
    return {
      title: '',
      linkedServiceID: '',
      sectionID: '',
      templateID: '',
      length: 1,
      banner: '',
      thumbnail: '',
      description: '',
      objectives: [] as ObjectivesReference[],
      references: [] as ObjectivesReference[],
    }
  }, [isEditMode, courseDetails, isLoading])

  let form = useForm({
    defaultValues,
    validators: {
      onMount: courseFormSchema,
      onChange: courseFormSchema,
    },
    onSubmit: ({ value }) => {
      // Determine which sections changed
      let dirtySections = getDirtySections(form, banner, thumbnail)

      // In create mode without courseID, save core and images (if present)
      let sectionsToSave =
        !isEditMode && !courseID
          ? ['core', ...(banner || thumbnail ? ['images'] : [])]
          : dirtySections

      // Use extracted submission hook
      return submitCourse(value, sectionsToSave)
    },
  })

  let selectedLinkedService = useStore(
    form.store,
    (state) => state.values.linkedServiceID,
  )

  let selectReferences = useMemo(
    () => selectReferencesByLinkedService(selectedLinkedService),
    [selectedLinkedService],
  )
  let referenceLib = useAppSelector(selectReferences)

  let selectCategories = useMemo(
    () => selectCategoriesByLinkedService(selectedLinkedService),
    [selectedLinkedService],
  )
  let sections = useAppSelector(selectCategories)

  let selectTemplates = useMemo(
    () => selectTemplatesByLinkedService(selectedLinkedService),
    [selectedLinkedService],
  )
  let templates = useAppSelector(selectTemplates)

  let selectCourseAccreditations = useMemo(
    () => selectAccreditationsByCourseID(courseID ?? ''),
    [courseID],
  )
  let courseAccreditations = useAppSelector(selectCourseAccreditations)
  let accreditation = courseAccreditations[0] ?? null

  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let allInstructors = useAppSelector(selectInstructors)
  let selectCourseInstructors = useMemo(
    () => selectInstructorsByCourseID(courseID ?? ''),
    [courseID],
  )
  let courseInstructors = useAppSelector(selectCourseInstructors)

  let availableInstructors = useMemo(() => {
    let courseInstructorIDs = new Set(courseInstructors.map((i) => i.id))
    return allInstructors.filter(
      (i) =>
        i.linkedServiceID === courseDetails?.linkedServiceID &&
        !courseInstructorIDs.has(i.id),
    )
  }, [allInstructors, courseInstructors, courseDetails?.linkedServiceID])

  useAutoFetch(
    isEditMode ? instructorsStatus : 'fulfilled',
    fetchAllInstructors,
  )

  let publishGuard = usePublishGuard({
    courseID,
    onUnpublishSuccess: () => {
      if (courseID) dispatch(fetchSelectedCourse(courseID))
      showSuccess('Course unpublished')
    },
    showError,
  })

  let {
    isInstructorSaving,
    pendingRemoveInstructor,
    setPendingRemoveInstructor,
    handleAddInstructor,
    handleCreateInstructor,
    handleEditInstructor,
    handleConfirmRemoveInstructor,
    handleReorderInstructors,
  } = useInstructorActions({
    courseID,
    linkedServiceID: courseDetails?.linkedServiceID,
    selectedEditInstructor,
    closeModal,
    dispatch,
    showSuccess,
    showError,
  })

  let formValues = useStore(form.store, (state) => state.values)
  let hasDirtyChanges =
    !!banner ||
    !!thumbnail ||
    formValues.title !== defaultValues.title ||
    formValues.linkedServiceID !== defaultValues.linkedServiceID ||
    formValues.sectionID !== defaultValues.sectionID ||
    formValues.templateID !== defaultValues.templateID ||
    formValues.length !== defaultValues.length ||
    formValues.description !== defaultValues.description ||
    JSON.stringify(formValues.objectives) !==
      JSON.stringify(defaultValues.objectives) ||
    JSON.stringify(formValues.references) !==
      JSON.stringify(defaultValues.references)

  let { canPublish, checks: publishChecks } = usePublishValidation(
    courseDetails,
    courseSteps,
    courseQuizzes,
    courseInstructors,
    accreditation,
    hasDirtyChanges,
  )

  let stepsLocked = isPublished && hasMigratableContent

  let [isChangingPublishState, setIsChangingPublishState] = useState(false)

  let handlePublishToggle = useCallback(() => {
    if (!courseID || isChangingPublishState) return
    let newStatus: CoursePublishedStatus = isPublished
      ? 'unpublished'
      : 'published'
    setIsChangingPublishState(true)
    updateCoursePublicationStatus(courseID, newStatus)
      .then(() => {
        dispatch(fetchSelectedCourse(courseID))
        showSuccess(isPublished ? 'Course unpublished' : 'Course published')
      })
      .catch(() =>
        showError(
          isPublished
            ? 'Failed to unpublish course'
            : 'Failed to publish course',
        ),
      )
      .finally(() => setIsChangingPublishState(false))
  }, [
    courseID,
    isPublished,
    isChangingPublishState,
    dispatch,
    showSuccess,
    showError,
  ])

  useEffect(() => {
    let currentSectionID = form.getFieldValue('sectionID')
    if (currentSectionID && !sections.some((s) => s.id === currentSectionID)) {
      form.setFieldValue('sectionID', '')
    }
  }, [selectedLinkedService, sections, form])

  useEffect(() => {
    let currentTemplateID = form.getFieldValue('templateID')
    if (
      currentTemplateID &&
      !templates.some((t) => t.id === currentTemplateID)
    ) {
      form.setFieldValue('templateID', '')
    }
  }, [selectedLinkedService, templates, form])

  useEffect(() => {
    if (isEditMode && courseDetails && !isLoading) {
      form.reset({
        title: courseDetails.name || '',
        linkedServiceID: courseDetails.linkedServiceID || '',
        sectionID: courseDetails.sectionID || '',
        templateID: courseDetails.templateID || '',
        length: courseDetails.length ?? 1,
        description: courseDetails.description || '',
        objectives: (courseDetails.objectives || []) as ObjectivesReference[],
        references: (courseDetails.references || []) as ObjectivesReference[],
        banner: '',
        thumbnail: '',
      })

      if (courseDetails.linkedServiceID) {
        dispatch(selectedLinkedServiceChanged(courseDetails.linkedServiceID))
      }
    }
  }, [isEditMode, courseDetails, isLoading, form])

  let templateToEdit = useMemo(
    () => templates.find((t) => t.id === selectedEditTemplateID) ?? undefined,
    [templates, selectedEditTemplateID],
  )

  let categoryToEdit = useMemo(
    () => sections.find((s) => s.id === selectedEditCategoryID) ?? undefined,
    [sections, selectedEditCategoryID],
  )

  let handleCategorySuccess = (sectionID: string) => {
    form.setFieldValue('sectionID', sectionID)
    showSuccess(
      categoryToEdit
        ? 'Category updated successfully'
        : 'Category created successfully',
    )
  }

  let handleCategoryFailure = () => {
    showError(
      categoryToEdit
        ? 'Failed to update category'
        : 'Failed to create category',
    )
  }

  let handleTemplateSuccess = (templateID: string) => {
    form.setFieldValue('templateID', templateID)
    showSuccess(
      templateToEdit
        ? 'Template updated successfully'
        : 'Template created successfully',
    )
  }

  let handleTemplateFailure = () => {
    showError(
      templateToEdit
        ? 'Failed to update template'
        : 'Failed to create template',
    )
  }

  let videoToEdit = useMemo(
    () => videos.find((v) => v.id === selectedEditVideoID) ?? undefined,
    [videos, selectedEditVideoID],
  )

  let videoToPreview = useMemo(
    () => videos.find((v) => v.id === selectedPreviewVideoID) ?? undefined,
    [videos, selectedPreviewVideoID],
  )

  let showNotesToPreview = useMemo(
    () =>
      showNotesLibrary.find((sn) => sn.id === selectedPreviewShowNotesID) ??
      undefined,
    [showNotesLibrary, selectedPreviewShowNotesID],
  )

  let availableVideosForCourse = useMemo(
    () =>
      videos.filter(
        (v) =>
          v.linkedServiceID === selectedLinkedService &&
          !v.courses.some((c) => c.courseID === courseID),
      ),
    [videos, selectedLinkedService, courseID],
  )

  let availableShowNotesForCourse = useMemo(
    () =>
      showNotesLibrary.filter(
        (sn) =>
          sn.linkedServiceID === selectedLinkedService &&
          !sn.courses.some((c) => c.courseID === courseID),
      ),
    [showNotesLibrary, selectedLinkedService, courseID],
  )

  let availableQuizzesForCourse = useMemo(
    () =>
      quizLibrary.filter(
        (q) =>
          q.linkedServiceID === selectedLinkedService &&
          !q.courses.some((c) => c.courseID === courseID),
      ),
    [quizLibrary, selectedLinkedService, courseID],
  )

  let handleShowNotesSuccess = useCallback(() => {
    if (!courseID) return
    showSuccess('Show notes added to course')
    dispatch(fetchShowNotesLibrary())
    dispatch(fetchCourseStepsByCourseID(courseID))
  }, [courseID, dispatch, showSuccess])

  let makeCourseBackState = () => ({
    backLabel: `Back to ${courseDetails?.name ?? 'Course'}`,
    backPath: CREATE_COURSE_URLS.EDIT(courseID ?? ''),
  })

  let handleNewShowNotesSuccess = useCallback(
    (showNotesID: string) => {
      if (!courseID) return
      dispatch(fetchShowNotesLibrary())
      dispatch(fetchCourseStepsByCourseID(courseID))
      navigate(CREATE_SHOW_NOTES_URLS.EDIT(showNotesID), {
        state: makeCourseBackState(),
      })
    },
    [courseID, courseDetails, dispatch, navigate],
  )

  let handleShowNotesFailure = useCallback(() => {
    showError('Failed to add show notes to course')
  }, [showError])

  let handleEditShowNotes = useCallback(
    (showNotesID: string) => {
      navigate(CREATE_SHOW_NOTES_URLS.EDIT(showNotesID), {
        state: makeCourseBackState(),
      })
    },
    [navigate, courseID, courseDetails],
  )

  let handleEditQuiz = useCallback(
    (quizID: string) => {
      navigate(CREATE_QUIZ_URLS.EDIT(quizID), { state: makeCourseBackState() })
    },
    [navigate, courseID, courseDetails],
  )

  let handleQuizSuccess = useCallback(() => {
    if (!courseID) return
    showSuccess('Quiz added to course')
    dispatch(fetchQuizLibrary())
    dispatch(fetchCourseStepsByCourseID(courseID))
  }, [courseID, dispatch, showSuccess])

  let handleNewQuizSuccess = useCallback(
    (quizID: string) => {
      if (!courseID) return
      dispatch(fetchQuizLibrary())
      dispatch(fetchCourseStepsByCourseID(courseID))
      navigate(CREATE_QUIZ_URLS.EDIT(quizID), { state: makeCourseBackState() })
    },
    [courseID, courseDetails, dispatch, navigate],
  )

  let handleQuizFailure = useCallback(() => {
    showError('Failed to add quiz to course')
  }, [showError])

  let handleVideoSuccess = useCallback(() => {
    if (!courseID) return
    showSuccess(
      videoToEdit ? 'Video updated successfully' : 'Video added to course',
    )
    dispatch(fetchVideoLibrary())
  }, [courseID, videoToEdit, dispatch, showSuccess])

  let handleVideoFailure = useCallback(() => {
    showError(videoToEdit ? 'Failed to update video' : 'Failed to create video')
  }, [videoToEdit, showError])

  let handleReferenceUpdated = (updatedRef: ObjectivesReference) => {
    showSuccess('Reference updated successfully')

    let currentReferences = form.getFieldValue('references') || []
    let updatedReferences = currentReferences.map((ref) =>
      ref.id === updatedRef.id ? { ...ref, text: updatedRef.text } : ref,
    )
    form.setFieldValue('references', updatedReferences)
  }

  let handleCancel = () => {
    navigate(CREATE_COURSE_URLS.INDEX)
  }

  let handleRemoveContentSuccess = (contentType: CreateContentTypes) => {
    if (!courseID) return

    let fetchAction =
      contentType === 'video'
        ? fetchVideoLibrary
        : contentType === 'showNotes'
          ? fetchShowNotesLibrary
          : fetchQuizLibrary

    dispatch(fetchAction())
    dispatch(fetchCourseStepsByCourseID(courseID))

    showSuccess(`${contentTypeToTextMap.get(contentType)} removed from course`)
  }

  let handleMigrateAccreditation = (accID: string) => {
    if (!accreditation || !courseID) return
    setIsMigratingAccreditation(true)
    migrateAccreditation(accID)
      .then(() => dispatch(fetchAllAccreditations()))
      .catch(() => showError('Failed to migrate accreditation'))
      .finally(() => setIsMigratingAccreditation(false))
  }

  let [pendingDeleteAccreditationID, setPendingDeleteAccreditationID] =
    useState<string | null>(null)

  let handleDeleteAccreditation = (accID: string) => {
    if (!courseID) return Promise.resolve()
    return removeCourseAccreditation(courseID, accID)
      .then(() => dispatch(fetchAllAccreditations()))
      .catch(() => showError('Failed to delete accreditation'))
      .finally(() => setPendingDeleteAccreditationID(null))
  }

  if (isLoading) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Box className="flex items-center gap-2 mb-6">
          <IconButton
            onClick={handleCancel}
            aria-label="Go back to Courses"
            sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">
            {isEditMode ? 'Edit Course' : 'Create Course'}
          </Typography>
        </Box>
        <Box className="flex flex-col items-center justify-center gap-3 py-24">
          <CircularProgress />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading course data...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center gap-2 mb-6">
        <IconButton
          onClick={handleCancel}
          aria-label="Go back to Courses"
          sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Course' : 'Create Course'}
        </Typography>
      </Box>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <Box className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <Box className="col-span-12 md:col-span-8">
            <Card
              className="mb-6"
              sx={{ bgcolor: 'background.paper' }}
              data-tour="create-course-basic-info-card"
            >
              <CardContent>
                <CardSectionHeader label="Basic Information" className="mb-6" />

                <Box className="flex flex-col gap-6">
                  <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {isEditMode ? (
                      <>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                            className="block mb-1"
                          >
                            Linked Service
                          </Typography>
                          <Typography variant="body1">
                            {linkedServices.find(
                              (s) => s.id === selectedLinkedService,
                            )?.name ?? '—'}
                          </Typography>
                        </Box>

                        <form.Field name="sectionID">
                          {(field) => (
                            <Box className="flex items-start gap-2">
                              <FormControl
                                fullWidth
                                required
                                error={shouldShowZodFieldError(
                                  field.state.meta,
                                )}
                              >
                                <InputLabel>Category</InputLabel>
                                <Select
                                  label="Category"
                                  value={field.state.value}
                                  onChange={(e) => {
                                    if (e.target.value === 'CREATE_NEW') {
                                      openModal('category')
                                    } else {
                                      field.handleChange(e.target.value)
                                    }
                                  }}
                                  onBlur={field.handleBlur}
                                  sx={paperInputSx}
                                >
                                  <MenuItem
                                    value="CREATE_NEW"
                                    className="font-semibold"
                                    sx={{ color: 'primary.main' }}
                                  >
                                    + Create New Category
                                  </MenuItem>
                                  {sections.map((section) => (
                                    <MenuItem
                                      key={section.id}
                                      value={section.id}
                                    >
                                      {section.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormFieldError fieldMeta={field.state.meta} />
                              </FormControl>
                              {field.state.value &&
                                field.state.value !== 'CREATE_NEW' && (
                                  <Tooltip title="Edit category">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        openEditCategoryModal(field.state.value)
                                      }
                                      sx={{ mt: 1 }}
                                      aria-label="Edit selected category"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </Box>
                          )}
                        </form.Field>
                      </>
                    ) : (
                      <>
                        <form.Field name="linkedServiceID">
                          {(field) => (
                            <FormControl
                              fullWidth
                              required
                              error={shouldShowZodFieldError(field.state.meta)}
                            >
                              <InputLabel>Linked Service</InputLabel>
                              <Select
                                label="Linked Service"
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e.target.value)
                                  dispatch(
                                    selectedLinkedServiceChanged(
                                      e.target.value,
                                    ),
                                  )
                                }}
                                onBlur={field.handleBlur}
                                sx={paperInputSx}
                              >
                                {linkedServices.map((service) => (
                                  <MenuItem key={service.id} value={service.id}>
                                    {service.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormFieldError fieldMeta={field.state.meta} />
                            </FormControl>
                          )}
                        </form.Field>

                        <form.Field name="sectionID">
                          {(field) => (
                            <Box className="flex items-start gap-2">
                              <FormControl
                                fullWidth
                                required
                                error={shouldShowZodFieldError(
                                  field.state.meta,
                                )}
                              >
                                <InputLabel>Category</InputLabel>
                                <Select
                                  label="Category"
                                  value={field.state.value}
                                  onChange={(e) => {
                                    if (e.target.value === 'CREATE_NEW') {
                                      openModal('category')
                                    } else {
                                      field.handleChange(e.target.value)
                                    }
                                  }}
                                  onBlur={field.handleBlur}
                                  disabled={!selectedLinkedService}
                                  sx={paperInputSx}
                                >
                                  <MenuItem
                                    value="CREATE_NEW"
                                    className="font-semibold"
                                    sx={{ color: 'primary.main' }}
                                  >
                                    + Create New Category
                                  </MenuItem>
                                  {sections.map((section) => (
                                    <MenuItem
                                      key={section.id}
                                      value={section.id}
                                    >
                                      {section.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {!selectedLinkedService && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                    className="mt-1 ml-3.5"
                                  >
                                    Select a linked service first
                                  </Typography>
                                )}
                                <FormFieldError fieldMeta={field.state.meta} />
                              </FormControl>
                              {field.state.value &&
                                field.state.value !== 'CREATE_NEW' && (
                                  <Tooltip title="Edit category">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        openEditCategoryModal(field.state.value)
                                      }
                                      sx={{ mt: 1 }}
                                      aria-label="Edit selected category"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </Box>
                          )}
                        </form.Field>
                      </>
                    )}
                  </Box>

                  <form.Field name="title">
                    {(field) => (
                      <TextField
                        label="Course Title"
                        required
                        fullWidth
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta)
                            ? getZodValidationError(field.state.meta.errors)
                            : undefined
                        }
                        sx={paperInputSx}
                      />
                    )}
                  </form.Field>

                  <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <form.Field name="length">
                      {(field) => (
                        <NumberField
                          label="Hours"
                          required
                          fullWidth
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v ?? 0)}
                          min={0}
                          step={0.5}
                          error={shouldShowZodFieldError(field.state.meta)}
                          helperText={
                            shouldShowZodFieldError(field.state.meta)
                              ? getZodValidationError(field.state.meta.errors)
                              : undefined
                          }
                          sx={paperInputSx}
                        />
                      )}
                    </form.Field>

                    <form.Field name="templateID">
                      {(field) => (
                        <Box className="flex items-start gap-2">
                          <FormControl
                            fullWidth
                            required
                            error={shouldShowZodFieldError(field.state.meta)}
                          >
                            <InputLabel>Template</InputLabel>
                            <Select
                              label="Template"
                              value={field.state.value}
                              onChange={(e) => {
                                if (e.target.value === 'CREATE_NEW') {
                                  openModal('template')
                                } else {
                                  field.handleChange(e.target.value)
                                }
                              }}
                              onBlur={field.handleBlur}
                              disabled={!selectedLinkedService}
                              sx={paperInputSx}
                            >
                              <MenuItem
                                value="CREATE_NEW"
                                className="font-semibold"
                                sx={{ color: 'primary.main' }}
                              >
                                + Create New Template
                              </MenuItem>
                              {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {!selectedLinkedService && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                                className="mt-1 ml-3.5"
                              >
                                Select a linked service first
                              </Typography>
                            )}
                            <FormFieldError fieldMeta={field.state.meta} />
                          </FormControl>
                          {field.state.value &&
                            field.state.value !== 'CREATE_NEW' && (
                              <Tooltip title="Edit template">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    openEditTemplateModal(field.state.value)
                                  }
                                  sx={{ mt: 1 }}
                                  aria-label="Edit selected template"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      )}
                    </form.Field>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              className="mb-6"
              sx={{ bgcolor: 'background.paper' }}
              data-tour="create-course-syllabus-card"
            >
              <CardContent>
                <CardSectionHeader label="Syllabus" className="mb-4" />

                {isEditMode && legacyTitleScreen && (
                  <Alert severity="info" className="mb-6">
                    <Typography variant="body2" className="mb-3">
                      This course has a title screen that is not compatible with
                      Courses V5. You can view and copy the content using the
                      button below, then use it in the new syllabus format
                      (Description, Objectives, References).
                    </Typography>
                    <Box className="flex gap-2">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openModal('title-screen-migration')}
                      >
                        View Title Screen Content
                      </Button>
                    </Box>
                  </Alert>
                )}

                <Box className="flex flex-col gap-6">
                  <form.Field name="description">
                    {(field) => (
                      <TextField
                        label="Description"
                        required
                        fullWidth
                        multiline
                        minRows={3}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta)
                            ? getZodValidationError(field.state.meta.errors)
                            : undefined
                        }
                        sx={paperInputSx}
                      />
                    )}
                  </form.Field>

                  <Box>
                    <form.Field name="objectives" mode="array">
                      {(field) => (
                        <Box
                          component="fieldset"
                          className="p-0 m-0 border-none"
                        >
                          <Box
                            component="legend"
                            className="flex items-center mb-4"
                          >
                            <Typography
                              variant="subtitle1"
                              component="span"
                              className="mr-2"
                            >
                              Objectives
                            </Typography>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => field.pushValue({ text: '' })}
                              aria-label="Add objective"
                            >
                              <Add />
                            </IconButton>
                          </Box>
                          {field.state.value.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                              className="mb-4"
                            >
                              This course does not have any objectives yet.
                            </Typography>
                          )}
                          <FormFieldError
                            fieldMeta={field.state.meta}
                            className="block mb-2"
                          />
                          <div
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="sr-only"
                          >
                            {field.state.value.length}{' '}
                            {field.state.value.length === 1
                              ? 'objective'
                              : 'objectives'}
                          </div>
                          <Box className="flex flex-col gap-4">
                            {field.state.value.map((_, i) => (
                              <form.Field
                                key={i}
                                name={`objectives[${i}].text`}
                              >
                                {(subField) => (
                                  <Box className="flex items-start gap-2">
                                    <TextField
                                      fullWidth
                                      label={`Objective ${i + 1}`}
                                      value={subField.state.value}
                                      onChange={(e) =>
                                        subField.handleChange(e.target.value)
                                      }
                                      onBlur={subField.handleBlur}
                                      error={shouldShowZodFieldError(
                                        subField.state.meta,
                                      )}
                                      helperText={
                                        shouldShowZodFieldError(
                                          subField.state.meta,
                                        )
                                          ? getZodValidationError(
                                              subField.state.meta.errors,
                                            )
                                          : undefined
                                      }
                                      sx={paperInputSx}
                                    />
                                    <IconButton
                                      onClick={() => field.removeValue(i)}
                                      aria-label={`Remove objective ${i + 1}`}
                                      color="error"
                                      className="mt-2"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                )}
                              </form.Field>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </form.Field>
                  </Box>

                  <Box>
                    <form.Field name="references" mode="array">
                      {(field) => (
                        <Box
                          component="fieldset"
                          className="p-0 m-0 border-none"
                        >
                          <Box
                            component="legend"
                            className="flex items-center mb-4"
                          >
                            <Typography
                              variant="subtitle1"
                              component="span"
                              className="mr-2"
                            >
                              References
                            </Typography>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => field.pushValue({ text: '' })}
                              aria-label="Add reference"
                            >
                              <Add />
                            </IconButton>
                            {selectedLinkedService && (
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() =>
                                  openModal('edit-library-references')
                                }
                                className="ml-auto normal-case"
                              >
                                Edit Library
                              </Button>
                            )}
                          </Box>
                          <Typography
                            id="references-instructions"
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                            className="block mb-2"
                          >
                            Select existing references from the library or type
                            to create new ones.
                          </Typography>
                          {field.state.value.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                              className="mb-4"
                            >
                              This course does not have any references yet.
                            </Typography>
                          )}
                          <FormFieldError
                            fieldMeta={field.state.meta}
                            className="block mb-2"
                          />
                          <div
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="sr-only"
                          >
                            {field.state.value.length}{' '}
                            {field.state.value.length === 1
                              ? 'reference'
                              : 'references'}
                          </div>
                          <Box className="flex flex-col gap-4">
                            {field.state.value.map((reference, i) => {
                              // Check if this reference is from the library (has an id)
                              let isLibraryReference = !!reference.id

                              return (
                                <form.Field
                                  key={i}
                                  name={`references[${i}].text`}
                                >
                                  {(subField) => (
                                    <Box className="flex items-start gap-2">
                                      <Autocomplete
                                        fullWidth
                                        freeSolo={!isLibraryReference}
                                        options={referenceLib.filter((ref) =>
                                          field.state.value.every(
                                            (r) => r.id !== ref.id,
                                          ),
                                        )}
                                        value={subField.state.value}
                                        readOnly={isLibraryReference}
                                        aria-describedby="references-instructions"
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label={`Reference ${i + 1}`}
                                            onBlur={subField.handleBlur}
                                            error={shouldShowZodFieldError(
                                              subField.state.meta,
                                            )}
                                            helperText={
                                              shouldShowZodFieldError(
                                                subField.state.meta,
                                              )
                                                ? getZodValidationError(
                                                    subField.state.meta.errors,
                                                  )
                                                : isLibraryReference
                                                  ? 'Library reference (read-only)'
                                                  : 'New Reference'
                                            }
                                            sx={paperInputSx}
                                            slotProps={{
                                              ...params.slotProps,

                                              input: {
                                                ...params.slotProps.input,
                                                readOnly: isLibraryReference,
                                              },
                                            }}
                                          />
                                        )}
                                        renderOption={(props, option) => {
                                          // eslint-disable-next-line fp/no-rest-parameters
                                          let { key, ...optionProps } = props
                                          return (
                                            <li key={key} {...optionProps}>
                                              {option.text}
                                            </li>
                                          )
                                        }}
                                        getOptionLabel={(option) =>
                                          typeof option === 'string'
                                            ? option
                                            : option.text || ''
                                        }
                                        onChange={(e, value) => {
                                          if (
                                            value &&
                                            typeof value === 'object'
                                          ) {
                                            // User selected existing reference from list
                                            form.setFieldValue(
                                              `references[${i}].id`,
                                              value.id,
                                            )
                                            form.setFieldValue(
                                              `references[${i}].text`,
                                              value.text,
                                            )
                                          } else if (
                                            value === null ||
                                            value === ''
                                          ) {
                                            // User cleared the field - remove id to allow free text
                                            form.setFieldValue(
                                              `references[${i}].id`,
                                              undefined,
                                            )
                                            form.setFieldValue(
                                              `references[${i}].text`,
                                              '',
                                            )
                                          } else if (
                                            typeof value === 'string'
                                          ) {
                                            // User typed new value - clear id to mark as custom
                                            form.setFieldValue(
                                              `references[${i}].id`,
                                              undefined,
                                            )
                                            form.setFieldValue(
                                              `references[${i}].text`,
                                              value,
                                            )
                                          }
                                        }}
                                        onInputChange={(e, value) => {
                                          // Only update text on typing, and only if not a library reference
                                          if (
                                            e?.type === 'change' &&
                                            !isLibraryReference
                                          ) {
                                            subField.handleChange(value)
                                          }
                                        }}
                                      />
                                      <IconButton
                                        onClick={() => field.removeValue(i)}
                                        aria-label={`Remove reference ${i + 1}`}
                                        color="error"
                                        sx={{ mt: 1 }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  )}
                                </form.Field>
                              )
                            })}
                          </Box>
                        </Box>
                      )}
                    </form.Field>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {isEditMode && courseID && (
              <MigratableContentList
                courseID={courseID}
                steps={legacySteps}
                courseVideos={videosNeedingMigration}
                courseShowNotes={showNotesNeedingMigration}
                courseQuizzes={quizzesNeedingMigration}
                legacyContent={{
                  textContent: legacyTextContent,
                }}
                isRefetching={
                  videoLibraryStatus === 'pending' ||
                  showNotesLibraryStatus === 'pending' ||
                  courseStepsStatus === 'pending' ||
                  isStepsSaving
                }
                locked={stepsLocked}
                onUnpublish={handlePublishToggle}
                unpublishDisabled={isChangingPublishState}
                callbacks={{
                  onMigrateSuccess: (contentType) => {
                    showSuccess('Content migrated successfully')
                    if (contentType === 'video') {
                      dispatch(fetchVideoLibrary())
                    } else if (contentType === 'showNotes') {
                      dispatch(fetchShowNotesLibrary())
                    } else if (contentType === 'quiz') {
                      dispatch(fetchQuizLibrary())
                    }
                    // Always refetch steps since migration adds new steps
                    dispatch(fetchCourseStepsByCourseID(courseID))
                  },
                  onViewTextContent: openTextContentModal,
                  onError: showError,
                }}
              />
            )}

            {isEditMode && courseID && (
              <Card
                sx={{ bgcolor: 'background.paper' }}
                data-tour="create-course-steps-section"
              >
                <CardContent>
                  <CardSectionHeader
                    label="Course Steps"
                    component="h3"
                    className="mb-4"
                  />
                  <CourseStepsSection
                    courseID={courseID}
                    courseName={courseDetails?.name}
                    steps={courseSteps}
                    stepsStatus={courseStepsStatus}
                    videos={courseVideos}
                    showNotes={courseShowNotes.filter((sn) => !sn.isMigratable)}
                    quizzes={courseQuizzes.filter((q) => !q.isMigratable)}
                    onSaveSuccess={() => {
                      dispatch(fetchCourseStepsByCourseID(courseID))
                      showSuccess('Course steps saved successfully')
                    }}
                    onError={showError}
                    onSavingStateChange={setIsStepsSaving}
                    onAddVideo={() => openModal('video')}
                    onAddShowNotes={openAddShowNotesModal}
                    onAddQuiz={() => openModal('quiz')}
                    hasQuiz={hasQuiz}
                    onEditVideo={openEditVideoModal}
                    onEditShowNotes={handleEditShowNotes}
                    onEditQuiz={handleEditQuiz}
                    onPreviewVideo={openVideoPreviewModal}
                    onPreviewShowNotes={openShowNotesPreviewModal}
                    onPreviewQuiz={openQuizPreviewModal}
                    locked={stepsLocked}
                    onUnpublish={handlePublishToggle}
                    unpublishDisabled={isChangingPublishState}
                    onRemoveContentSuccess={handleRemoveContentSuccess}
                    isPublished={isPublished}
                    publishGuard={publishGuard}
                  />
                </CardContent>
              </Card>
            )}
          </Box>

          <Box className="col-span-12 md:col-span-4">
            {isEditMode && (
              <Card
                className="mb-6"
                sx={(theme) => ({
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette[isPublished ? 'success' : 'warning'].main, 0.3)}`,
                  borderTop: `3px solid ${theme.palette[isPublished ? 'success' : 'warning'].main}`,
                })}
                data-tour="create-course-publish-card"
              >
                <CardContent>
                  <CardSectionHeader
                    label="Publish Status"
                    component="h3"
                    className="mb-4"
                  />
                  <Box className="flex items-center justify-between mb-4">
                    <Typography variant="body2">Status:</Typography>
                    <Chip
                      label={isPublished ? 'Published' : 'Draft'}
                      color={isPublished ? 'success' : 'warning'}
                      size="medium"
                    />
                  </Box>
                  {!isPublished && (
                    <Box className="flex flex-col gap-2 mb-4">
                      <PublishChecklistItem
                        done={publishChecks.basicInfo}
                        label="Basic information"
                      />
                      <PublishChecklistItem
                        done={publishChecks.syllabus}
                        label="Syllabus"
                      />
                      <PublishChecklistItem
                        done={publishChecks.hasContentStep}
                        label="At least one video or show notes step"
                      />
                      <PublishChecklistItem
                        done={publishChecks.accreditation}
                        label="Accreditation"
                      />
                      <PublishChecklistItem
                        done={publishChecks.hasInstructor}
                        label="At least one instructor"
                      />
                      <PublishChecklistItem
                        done={publishChecks.noUnsavedChanges}
                        label="No unsaved changes"
                      />
                      <PublishChecklistItem
                        done={publishChecks.quizzesHaveQuestions}
                        label="Quiz has at least one question"
                      />
                      <PublishChecklistItem
                        done={publishChecks.lastStepIsQuiz}
                        label="Last step is a quiz"
                      />
                    </Box>
                  )}
                  <Tooltip
                    title={
                      !isPublished && !canPublish
                        ? 'Complete all requirements before publishing'
                        : ''
                    }
                  >
                    <span>
                      <Button
                        fullWidth
                        variant={isPublished ? 'outlined' : 'contained'}
                        color={isPublished ? 'warning' : 'success'}
                        onClick={handlePublishToggle}
                        disabled={
                          (!isPublished && !canPublish) ||
                          isChangingPublishState
                        }
                      >
                        {isChangingPublishState
                          ? 'Saving...'
                          : isPublished
                            ? 'Unpublish'
                            : 'Publish'}
                      </Button>
                    </span>
                  </Tooltip>
                </CardContent>
              </Card>
            )}

            <Card
              className="mb-6"
              sx={{ bgcolor: 'background.paper' }}
              data-tour="create-course-images-card"
            >
              <CardContent>
                <CardSectionHeader
                  label="Course Images"
                  component="h3"
                  className="mb-4"
                />

                <Box className="flex flex-col gap-4">
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                      className="mb-2"
                    >
                      Banner Image (4:1 aspect ratio)
                    </Typography>
                    <ImageUpload
                      id="course-banner"
                      type="banner"
                      image={banner}
                      setImage={setBanner}
                      existingImage={existingBannerImage}
                      imageBasePath={bannerImageBasePath}
                      sxOverride={{
                        backgroundColor: 'background.paperDark',
                        borderColor: 'divider',
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                      className="mb-2"
                    >
                      Thumbnail Image (16:9 aspect ratio)
                    </Typography>
                    <ImageUpload
                      id="course-thumbnail"
                      type="thumbnail"
                      image={thumbnail}
                      setImage={setThumbnail}
                      existingImage={existingThumbnailImage}
                      imageBasePath={thumbnailImageBasePath}
                      sxOverride={{
                        backgroundColor: 'background.paperDark',
                        borderColor: 'divider',
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {isEditMode && (
              <Card
                className="mb-6"
                sx={{ bgcolor: 'background.paper' }}
                data-tour="create-course-accreditations-section"
              >
                <CardContent>
                  <CardSectionHeader
                    label="Accreditations"
                    component="h3"
                    className="mb-4"
                    tooltipText="Accreditation type is determined by the linked service under their own criteria and is separate from FOAMfrat accredited content. Linked service content does not report to any accrediting body and will not appear on your CE transcript."
                  />
                  <AccreditationsSection
                    accreditation={accreditation}
                    isPublished={isPublished}
                    isMigrating={isMigratingAccreditation}
                    onMigrate={(accID) => handleMigrateAccreditation(accID)}
                    onEdit={(acc) =>
                      openAccreditationModal(acc.accreditationType, acc)
                    }
                    onAdd={(type) => openAccreditationModal(type)}
                    onDelete={(accID) => setPendingDeleteAccreditationID(accID)}
                    onDeletePublished={(accID) =>
                      publishGuard.triggerGuard(
                        true,
                        'Deleting this accreditation will violate publish requirements.',
                        () => setPendingDeleteAccreditationID(accID),
                      )
                    }
                    onUnpublish={handlePublishToggle}
                    unpublishDisabled={isChangingPublishState}
                  />
                </CardContent>
              </Card>
            )}

            {isEditMode && courseID && (
              <Card
                className="mb-6"
                sx={{ bgcolor: 'background.paper' }}
                data-tour="create-course-instructors-section"
              >
                <CardContent>
                  <CardSectionHeader
                    label="Instructors"
                    component="h3"
                    className="mb-4"
                    tooltipText="To create a new instructor, type their name in the search field and select the Create option that appears."
                  />
                  <InstructorsSection
                    courseInstructors={courseInstructors}
                    availableInstructors={availableInstructors}
                    isSaving={isInstructorSaving}
                    onAdd={handleAddInstructor}
                    onCreateNew={openCreateInstructorModal}
                    onEdit={openEditInstructorModal}
                    onRemove={(instructor) => {
                      let wouldViolate =
                        isPublished && courseInstructors.length <= 1
                      publishGuard.triggerGuard(
                        wouldViolate,
                        'Removing this instructor will violate publish requirements.',
                        () => setPendingRemoveInstructor(instructor),
                      )
                    }}
                    onReorder={handleReorderInstructors}
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        <Box
          className="sticky bottom-0 z-10 flex justify-end gap-3 py-4 mt-8 border-t"
          sx={{
            bgcolor: 'background.default',
            borderColor: 'divider',
          }}
        >
          <form.Subscribe
            selector={(state) => ({
              isSubmitting: state.isSubmitting,
              // Subscribe only to specific fields we validate (not all values)
              linkedServiceID: state.values.linkedServiceID,
              sectionID: state.values.sectionID,
              title: state.values.title,
              length: state.values.length,
              templateID: state.values.templateID,
              description: state.values.description,
              objectives: state.values.objectives,
              references: state.values.references,
            })}
          >
            {({
              isSubmitting,
              linkedServiceID,
              sectionID,
              title,
              length,
              templateID,
              description,
              objectives,
              references,
            }) => {
              // Build field values object for validation
              let fieldValues = {
                linkedServiceID,
                sectionID,
                title,
                length,
                templateID,
                description,
                objectives,
                references,
              }

              // Determine which sections are dirty
              let dirtySections = getDirtySections(form, banner, thumbnail)

              // In create mode without courseID, allow core and images
              let availableSections =
                !isEditMode && !courseID
                  ? dirtySections.filter((s) => s === 'core' || s === 'images')
                  : dirtySections

              // Enable save if any section is dirty AND valid
              let canSave =
                availableSections.length > 0 &&
                validateSections(fieldValues, availableSections)

              let buttonText = isSubmitting
                ? 'Saving...'
                : isEditMode
                  ? 'Update Course'
                  : courseID
                    ? 'Save Changes'
                    : 'Create Course'

              return (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    size="large"
                    startIcon={<Cancel />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !canSave}
                    size="large"
                    aria-label={buttonText}
                    aria-busy={isSubmitting}
                    startIcon={<ConfirmIcon />}
                    onClick={() => form.handleSubmit()}
                    data-tour="create-course-save-button"
                  >
                    {buttonText}
                  </Button>
                </>
              )
            }}
          </form.Subscribe>
        </Box>
      </form>
      {activeModal === 'category' && (
        <Suspense fallback={<CircularProgress />}>
          <CategoryModal
            isOpen={activeModal === 'category'}
            onClose={closeModal}
            selectedLinkedService={selectedLinkedService}
            dispatch={dispatch}
            onSuccess={handleCategorySuccess}
            onFailure={handleCategoryFailure}
            categoryToEdit={categoryToEdit}
          />
        </Suspense>
      )}
      {activeModal === 'template' && (
        <Suspense fallback={<CircularProgress />}>
          <TemplateModal
            isOpen={activeModal === 'template'}
            onClose={closeModal}
            selectedLinkedService={selectedLinkedService}
            dispatch={dispatch}
            onSuccess={handleTemplateSuccess}
            onFailure={handleTemplateFailure}
            templateToEdit={templateToEdit}
          />
        </Suspense>
      )}
      {isEditMode &&
        legacyTitleScreen &&
        activeModal === 'title-screen-migration' && (
          <Suspense fallback={<CircularProgress />}>
            <CourseTitleMigrationScreenModal
              isOpen={activeModal === 'title-screen-migration'}
              onClose={closeModal}
              titleScreen={legacyTitleScreen}
              onCopySuccess={(fieldName) =>
                showSuccess(`${fieldName} has been copied to clipboard`)
              }
            />
          </Suspense>
        )}
      {isEditMode &&
        hasMigratableContent &&
        legacyTextContent.length > 0 &&
        activeModal === 'migrate-text-content' && (
          <Suspense fallback={<CircularProgress />}>
            <MigrateTextContentModal
              isOpen={activeModal === 'migrate-text-content'}
              onClose={closeModal}
              textContent={
                selectedTextContentID
                  ? legacyTextContent.filter(
                      (tc) => tc.id === selectedTextContentID,
                    )
                  : legacyTextContent
              }
              onCopySuccess={(itemName) =>
                showSuccess(`${itemName} content has been copied to clipboard`)
              }
            />
          </Suspense>
        )}
      {activeModal === 'edit-library-references' && (
        <Suspense fallback={<CircularProgress />}>
          <EditLibraryReferencesModal
            isOpen={activeModal === 'edit-library-references'}
            onClose={closeModal}
            linkedServiceID={selectedLinkedService}
            onReferenceUpdated={handleReferenceUpdated}
            onError={showError}
          />
        </Suspense>
      )}
      {activeModal === 'video' && isEditMode && courseID && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <VideoModal
            isOpen
            onClose={closeModal}
            courseID={courseID}
            videos={availableVideosForCourse}
            onSuccess={handleVideoSuccess}
            onFailure={handleVideoFailure}
            videoToEdit={videoToEdit}
          />
        </Suspense>
      )}
      {activeModal === 'video-preview' && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <VideoPreviewModal
            isOpen
            onClose={closeModal}
            video={videoToPreview}
          />
        </Suspense>
      )}
      {activeModal === 'show-notes-preview' && showNotesToPreview && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <ShowNotesPreviewModal
            isOpen
            onClose={closeModal}
            showNotes={showNotesToPreview}
          />
        </Suspense>
      )}
      {activeModal === 'quiz-preview' && selectedPreviewQuizID && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <QuizPreviewModal
            isOpen
            onClose={closeModal}
            quizID={selectedPreviewQuizID}
            onEditQuiz={handleEditQuiz}
          />
        </Suspense>
      )}
      {activeModal === 'show-notes' && isEditMode && courseID && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <ShowNotesModal
            isOpen
            onClose={closeModal}
            selectedLinkedService={selectedLinkedService}
            courseID={courseID}
            showNotesList={availableShowNotesForCourse}
            onSuccess={handleShowNotesSuccess}
            onNewShowNotesSuccess={handleNewShowNotesSuccess}
            onFailure={handleShowNotesFailure}
          />
        </Suspense>
      )}
      {activeModal === 'quiz' && isEditMode && courseID && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <QuizModal
            isOpen
            onClose={closeModal}
            selectedLinkedService={selectedLinkedService}
            courseID={courseID}
            quizList={availableQuizzesForCourse}
            hasQuiz={hasQuiz}
            onSuccess={handleQuizSuccess}
            onNewQuizSuccess={handleNewQuizSuccess}
            onFailure={handleQuizFailure}
          />
        </Suspense>
      )}
      {activeModal === 'accreditation' && isEditMode && courseID && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <AccreditationModal
            isOpen
            courseID={courseID}
            linkedServiceID={courseDetails?.linkedServiceID ?? ''}
            accreditation={selectedAccreditation?.acc}
            accreditationType={
              selectedAccreditation?.accreditationType || 'accredited'
            }
            onClose={closeModal}
            onSuccess={(msg) => showSuccess(msg)}
            onError={(msg) => showError(msg)}
          />
        </Suspense>
      )}
      <ConfirmDialog
        isOpen={!!pendingDeleteAccreditationID}
        title="Delete Accreditation"
        message="Are you sure you want to delete this accreditation? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={() =>
          pendingDeleteAccreditationID
            ? handleDeleteAccreditation(pendingDeleteAccreditationID)
            : Promise.resolve()
        }
        onClose={() => setPendingDeleteAccreditationID(null)}
      />
      {(isCreateInstructorModalOpen || isEditInstructorModalOpen) && (
        <Suspense
          fallback={
            <Box className="fixed inset-0 z-50 flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
        >
          <InstructorModal
            key={selectedEditInstructor?.id ?? 'create'}
            isOpen
            instructor={selectedEditInstructor ?? undefined}
            prefillName={
              isCreateInstructorModalOpen
                ? createInstructorPrefillName
                : undefined
            }
            isSaving={isInstructorSaving}
            onClose={closeModal}
            onSubmit={
              isEditInstructorModalOpen
                ? handleEditInstructor
                : handleCreateInstructor
            }
          />
        </Suspense>
      )}
      <ConfirmDialog
        isOpen={!!pendingRemoveInstructor}
        title="Remove Instructor"
        message={`Are you sure you want to remove ${pendingRemoveInstructor?.name} from this course?`}
        confirmLabel="Remove"
        onConfirm={handleConfirmRemoveInstructor}
        onClose={() => setPendingRemoveInstructor(null)}
      />
      <UnpublishRequiredDialog
        open={publishGuard.dialogOpen}
        message={publishGuard.dialogMessage}
        onConfirm={publishGuard.handleConfirm}
        onCancel={publishGuard.handleCancel}
        isLoading={publishGuard.isUnpublishing}
      />
      {NotificationComponent}
    </Container>
  )
}
